
import { defineStore } from 'pinia';
import VideoApi, {
  type GetVideosRequestBody,
  type UpdateVideoRequestBody,
  type UploadVideoRequestBody
} from '@/api/Video';
import { useFetchApi } from '@/composables/common/useFetchApi';
import type {
  ResumedUploadItem,
  UploadData,
  Video,
  WorkerPayloadFile,
  WorkerResumeUploadMessage,
  WorkerStartUploadMessage
} from '@/types/';
import { computed, ref, watch } from 'vue';
import { ApiError } from '@/errors/apiError';
import { VIDEO_LIST_PAGE_LIMIT, UPLOAD_CHUNK_SIZE } from '@/utils/constants';
import { getAirbrake } from '@/utils/airbrake';

import Worker from '@/worker?worker';

interface label {
  _id: string;
  tags: [];
}

const bytesToMb = (bytes: number) => Math.round((bytes / (1024 * 1024)) * 100) / 100;

export const useVideoStore = defineStore('video', () => {
  /**
   * state
   */
  const videoList = ref<Video[]>([]);
  const labelList = ref<label[]>([]);
  const totalVideoCount = ref<number>(0);
  const currentVideo = ref<Video | null>(null);
  const currentUploads = ref<Record<string, UploadData>>({});
  // Global upload queue to ensure uploads are processed strictly in order
  const uploadQueue = ref<
    {
      params: UploadVideoRequestBody;
      files: File[];
      resolve: (value?: unknown) => void;
      reject: (reason?: unknown) => void;
    }[]
  >([]);
  const isProcessingQueue = ref(false);
  const videoListCondition = ref<GetVideosRequestBody>({
    start: '',
    end: '',
    keyword: '',
    group_id: '',
    skip_cache: false,
    page: 1,
    limit: VIDEO_LIST_PAGE_LIMIT
  });

  /**
   * getter
   */
  const getTotalVideoCount = computed(() => {
    return totalVideoCount.value;
  });

  const totalPages = computed(() => {
    const limit = videoListCondition.value.limit ?? VIDEO_LIST_PAGE_LIMIT;
    if (limit <= 0) return 1;
    return Math.ceil(totalVideoCount.value / limit) || 1;
  });

  const resolveUploadProgressAndSize = (options?: {
    videoId?: number | string;
    files?: File[];
  }) => {
    const videoIdStr = options?.videoId != null ? String(options.videoId) : undefined;
    const uploadData = videoIdStr ? currentUploads.value[videoIdStr] : undefined;

    let progress_percent = uploadData?.progress ?? 0;
    let totalBytes = 0;

    if (uploadData?.uploads?.length) {
      totalBytes = uploadData.uploads.reduce(
        (sum, u) => sum + (u.size || u.file?.size || 0),
        0
      );
      if (uploadData.progress == null && uploadData.uploads.length > 0) {
        const sumProgress = uploadData.uploads.reduce((sum, u) => sum + (u.progress || 0), 0);
        progress_percent = Math.round(sumProgress / uploadData.uploads.length);
      }
    } else if (options?.files?.length) {
      totalBytes = options.files.reduce((sum, f) => sum + f.size, 0);
    }

    return {
      progress_percent,
      file_size_mb: bytesToMb(totalBytes)
    };
  };

  const notifyUploadError = (
    error: unknown,
    detectionSource: string,
    options?: {
      videoId?: number | string;
      files?: File[];
      uploadId?: number;
      extra?: Record<string, unknown>;
    }
  ) => {
    const { progress_percent, file_size_mb } = resolveUploadProgressAndSize(options);
    const err =
      error instanceof Error
        ? error
        : new Error(typeof error === 'string' ? error : `Video upload error: ${detectionSource}`);

    getAirbrake()?.notify({
      error: err,
      params: {
        category: 'video_upload',
        detection_source: detectionSource,
        progress_percent,
        file_size_mb,
        video_id: options?.videoId != null ? String(options.videoId) : undefined,
        upload_id: options?.uploadId ?? undefined,
        ...(options?.extra ?? {})
      }
    });
  };

  // Prevent accidental page reload/close while the upload queue is processing
  const beforeUnloadHandler = (e: BeforeUnloadEvent) => {
    e.preventDefault();
    // Chrome requires returnValue to be set
    e.returnValue = 'Upload is in progress. Are you sure you want to leave?';
  };

  watch(
    isProcessingQueue,
    (processing) => {
      if (typeof window === 'undefined') return;
      if (processing) {
        window.addEventListener('beforeunload', beforeUnloadHandler);
      } else {
        window.removeEventListener('beforeunload', beforeUnloadHandler);
      }
    },
    { immediate: true }
  );

  /**
   * actions ToDo: BE側で試合ありの動画のみ返せるように、パラメータで選択できるようにする
   */
  const VIDEO_LIST_CACHE_TTL_MS = 60 * 1000;
  const videoListCache = ref<{
    params: GetVideosRequestBody;
    data: Video[];
    at: number;
  } | null>(null);

  const isCacheValid = (
    cached: typeof videoListCache.value | null,
    params: GetVideosRequestBody
  ) => {
    if (!cached) return false;
    if (Date.now() - cached.at > VIDEO_LIST_CACHE_TTL_MS) return false;
    return JSON.stringify(cached.params) === JSON.stringify(params);
  };

  const fetchVideoList = async (params?: GetVideosRequestBody, hideProgress = false) => {
    const { fetchData, error, data } = useFetchApi(hideProgress);
    const requestParams = params ?? videoListCondition.value;

    // キャッシュヒット: TTL内かつ同一パラメータ
    if (!requestParams.skip_cache && isCacheValid(videoListCache.value, requestParams)) {
      videoList.value = videoListCache.value!.data;
      return;
    }

    await fetchData(() => VideoApi.getVideoList(requestParams));
    if (error.value) {
      throw error.value;
    }
    videoListCondition.value.skip_cache = false;
    const response = data.value;

    if (Array.isArray(response?.data?.videos) && typeof response?.data?.total === 'number') {
      videoList.value = response.data.videos;
      totalVideoCount.value = response.data.total;

      // Check if any video has status 'enc', 'up_end' and set skip_cache to true
      const hasEncodingVideo = response.data.videos.some((video: Video) =>
        ['enc', 'up_end'].includes(video.data.status)
      );
      if (hasEncodingVideo) {
        videoListCondition.value.skip_cache = true;
        const reloadTiming = response.data.videos.some(
          (video: Video) => video.data.status == 'up_end'
        )
          ? 1
          : 15;
        setTimeout(() => {
          fetchVideoList(params, true);
        }, 60000 * reloadTiming);
      }

      // 取得結果をキャッシュ
      videoListCache.value = {
        params: { ...requestParams, skip_cache: false },
        data: response.data.videos,
        at: Date.now()
      };
    } else {
      throw new ApiError(500, 'Invalid response format');
    }
  };

  const fetchVideo = async (video_id: number, include_data = false) => {
    const { fetchData, error, data } = useFetchApi();
    await fetchData(() => VideoApi.getVideo(video_id, include_data));
    if (error.value) {
      throw error.value;
    }
    const response = data.value;
    if (response?.data) {
      currentVideo.value = response.data;
    } else {
      throw new ApiError(500, 'Invalid response format');
    }
  };

  // =======================================================
  // 1. INITIALIZE THE SINGLETON WORKER
  // =======================================================
  const worker = new Worker();

  worker.onmessage = async (event: MessageEvent) => {
    const msg = event.data;
    const video_id_str = msg.videoId?.toString();
    const uploadItem = video_id_str && currentUploads.value[video_id_str]?.uploads[msg.fileIndex];

    switch (msg.type) {
      case 'PENDING_UPLOADS_FOUND':
        if (msg.uploadIds.length > 0) {
          console.log(
            `Found ${msg.uploadIds.length} interrupted uploads. Validating with server...`
          );

          // 1. Create an array to collect valid IDs
          const validUploadIds: number[] = [];

          for (const idStr of msg.uploadIds) {
            const upload_id = parseInt(idStr);

            try {
              const { fetchData, error } = useFetchApi();
              await fetchData(() => VideoApi.getUpload(upload_id));

              if (error.value && error.value.statusCode === 404) {
                worker.postMessage({ type: 'GARBAGE_COLLECT', upload_id });
              } else if (!error.value) {
                // 2. If it is valid, add it to our array
                validUploadIds.push(upload_id);
              }
            } catch (e) {
              console.error(`Network error validating upload ${upload_id}.`, e);
              notifyUploadError(e, 'pending-upload-validate', {
                uploadId: upload_id,
                extra: { upload_id }
              });
            }
          }

          // 3. Send them ALL AT ONCE to the worker!
          if (validUploadIds.length > 0) {
            worker.postMessage({
              type: 'RESUME_BATCH',
              uploadIds: validUploadIds
            } as WorkerResumeUploadMessage);
          }
        }
        break;

      case 'HASH_COMPLETE':
        try {
          const hashResponse = await VideoApi.checkVideoHash(msg.videoId, msg.hash);
          worker.postMessage({
            type: 'HASH_CHECK_RESULT',
            videoId: msg.videoId,
            exists: hashResponse.data.exists
          });
        } catch (_e) {
          worker.postMessage({ type: 'HASH_CHECK_RESULT', videoId: msg.videoId, exists: false });
        }
        break;

      case 'HASH_PROGRESS':
        // 1. Safety net: Recreate state if it doesn't exist
        if (!currentUploads.value[video_id_str]) {
          currentUploads.value[video_id_str] = {
            video_id: msg.videoId,
            progress: msg.progress,
            status: 'hashing',
            uploads: []
          } as UploadData;
        }

        // 2. Update the GLOBAL progress (This powers the VideoCard!)
        currentUploads.value[video_id_str].progress = msg.progress;

        // 3. Update the child item if it exists
        if (uploadItem) {
          uploadItem.status = 'hashing';
          uploadItem.progress = msg.progress;
        }
        break;

      case 'UPLOAD_PROGRESS':
        // 1. Safety net: Recreate state if it doesn't exist
        if (!currentUploads.value[video_id_str]) {
          currentUploads.value[video_id_str] = {
            video_id: msg.videoId,
            progress: msg.progress,
            status: 'up',
            uploads: [
              // Then use it in the code block:
              {
                upload_id: msg.uploadId || 0,
                status: 'up',
                size: 0,
                chunk: msg.fileIndex + 1,
                chunks: 1,
                progress: msg.progress,
                file: new File([], 'Resumed Upload.mp4', { type: 'video/mp4' })
              } as ResumedUploadItem
            ]
          } as UploadData;
        }

        // 2. Update the GLOBAL progress
        currentUploads.value[video_id_str].progress = msg.progress;

        // 3. Update the child item if it exists
        if (uploadItem) {
          uploadItem.progress = msg.progress;
          uploadItem.status = 'up';
        }
        break;

      case 'FILE_COMPLETE': {
        const maxRetries = 3;
        let success = false;
        let lastManifestError: unknown;

        // We can use await directly because worker.onmessage is async!
        for (let attempt = 1; attempt <= maxRetries; attempt++) {
          try {
            await VideoApi.postUploadURL(msg.uploadId, {
              status: 'up_end',
              manifest: msg.manifest
            });
            success = true;
            break; // Break out of the loop immediately on success
          } catch (e) {
            lastManifestError = e;
            console.warn(
              `[Attempt ${attempt}/${maxRetries}] Failed to send manifest to backend.`,
              e
            );
            if (attempt < maxRetries) {
              console.log(`Waiting 3 seconds before retrying manifest upload...`);
              await new Promise((resolve) => setTimeout(resolve, 3000));
            }
          }
        }

        if (success) {
          // Update UI
          if (uploadItem) {
            uploadItem.status = 'up_end';
            uploadItem.progress = 100;
          }
          // Tell the worker it is safe to delete the OPFS folder
          worker.postMessage({
            type: 'MANIFEST_SAVE_RESULT',
            uploadId: msg.uploadId,
            success: true
          });
        } else {
          // Update UI to show error
          if (uploadItem) uploadItem.status = 'up_err';
          notifyUploadError(
            lastManifestError ?? new Error('Manifest save failed'),
            'manifest-save',
            {
              videoId: msg.videoId,
              uploadId: msg.uploadId,
              extra: { file_index: msg.fileIndex }
            }
          );
          // Tell the worker it failed so it aborts and preserves the OPFS folder
          worker.postMessage({
            type: 'MANIFEST_SAVE_RESULT',
            uploadId: msg.uploadId,
            success: false
          });
        }
        break;
      }

      case 'ALL_FILES_COMPLETE':
        if (msg.deduplicated && currentUploads.value[video_id_str]) {
          currentUploads.value[video_id_str].uploads.forEach((u) => {
            u.status = 'up_end';
            u.progress = 100;
          });
        }
        if (currentUploads.value[video_id_str]) {
          currentUploads.value[video_id_str].progress = 100;
          delete currentUploads.value[video_id_str];
        }
        videoListCondition.value.skip_cache = true;
        await fetchVideoList();
        break;

      case 'ERROR':
        console.error('Worker Error:', msg.error);
        if (uploadItem) {
          uploadItem.status = 'up_err';
        }
        notifyUploadError(
          msg.error instanceof Error
            ? msg.error
            : new Error(typeof msg.error === 'string' ? msg.error : 'Worker upload error'),
          'worker-error',
          {
            videoId: msg.videoId,
            uploadId: uploadItem ? uploadItem.upload_id : msg.uploadId,
            extra: {
              file_index: msg.fileIndex,
              worker_error: msg.error
            }
          }
        );
        try {
          if (uploadItem) {
            await VideoApi.postUploadURL(
              uploadItem.upload_id,
              { status: 'up_err' },
              { skipErrorToast: true }
            );
          }
          if (msg.videoId) {
            await VideoApi.updateVideo(msg.videoId, { status: 'up_err' }, { skipErrorToast: true });
          }
        } catch (e) {
          // up_err への更新失敗は汎用500トーストにしない（Airbrake のみ）
          notifyUploadError(e, 'worker-error-status-update', {
            videoId: msg.videoId,
            uploadId: uploadItem ? uploadItem.upload_id : msg.uploadId,
            extra: { video_status: 'up_err' }
          });
        }
        break;
    }
  };

  // Internal worker that performs the upload task
  const doUpload = async (params: UploadVideoRequestBody, files: File[]) => {
    const { fetchData, error, data } = useFetchApi();
    let createdVideoId: number | undefined;

    try {
      // 1. Create the initial video record in the database
      await fetchData(() => VideoApi.uploadVideo(params));
      if (error.value) {
        throw error.value;
      }

      interface ResponseInterface {
        video: {
          video_id: number;
        };
      }

      const response = data.value as unknown as ResponseInterface;
      console.log('Video upload task created successfully', response);

      if (response?.video) {
        const { video_id } = response.video;
        createdVideoId = video_id;
        const video_id_str = video_id.toString();

        // 2. Initialize the reactive store state matching your UploadData interface exactly
        const uploads = files.map((file, idx) => {
          return {
            upload_id: 0, // Will be populated in the next loop
            file,
            chunk: idx + 1,
            chunks: files.length,
            status: 'init',
            size: file.size,
            progress: 0
          } as ResumedUploadItem;
        });

        currentUploads.value[video_id_str] = {
          video_id,
          progress: 0,
          uploads
        } as UploadData;

        // 3. Pre-fetch Swift TempURLs for ALL files upfront
        const workerPayloadFiles = []; // Array to hold the data we'll pass to the worker

        for (let idx = 0; idx < currentUploads.value[video_id_str].uploads.length; idx++) {
          const upload = currentUploads.value[video_id_str].uploads[idx];
          if (!upload) {
            continue;
          }
          const microChunksCount = Math.ceil(upload.size / UPLOAD_CHUNK_SIZE);

          try {
            const urlRes = await VideoApi.postUploadURL(0, {
              status: 'init',
              filename: upload.file.name,
              size: upload.size,
              chunk: upload.chunk, // the file index
              chunks: upload.chunks, // total files
              vid: video_id,
              micro_chunks: microChunksCount // Tell Django how many URLs we need for this file
            });

            if (urlRes?.data.upload && urlRes?.data.puts) {
              // Update the reactive state with the DB record ID
              currentUploads.value[video_id_str].uploads[idx].upload_id =
                urlRes.data.upload.upload_id;

              // Push the data the worker needs to a separate array
              workerPayloadFiles.push({
                file: upload.file,
                urls: urlRes.data.puts, // Array of pre-generated TempURLs
                upload_id: urlRes.data.upload.upload_id,
                video_id
              } as WorkerPayloadFile);
            }
          } catch (e: unknown) {
            console.error(`Failed to init upload URLs for file ${upload.file.name}`, e);
            currentUploads.value[video_id_str].uploads[idx].status = 'up_err';
            throw e; // Break the upload process if we can't get URLs
          }
        }

        // Just send the message to our single shared worker!
        worker.postMessage({
          type: 'START_UPLOAD',
          videoId: video_id, // Pass video_id explicitly
          files: workerPayloadFiles
        } as WorkerStartUploadMessage);
      } else {
        throw new ApiError(500, 'Invalid response format');
      }
    } catch (err) {
      notifyUploadError(err, 'do-upload', {
        videoId: createdVideoId,
        files,
        extra: {
          filename: files[0]?.name,
          file_count: files.length
        }
      });
      throw err;
    }
  };
  // =======================================================
  // 2. SIMPLIFIED ACTIONS
  // =======================================================

  const resumePendingUploads = () => {
    worker.postMessage({ type: 'CHECK_PENDING' });
  };

  // Queue processor - guarantees strictly-serial execution
  const processUploadQueue = async () => {
    if (isProcessingQueue.value) return;
    isProcessingQueue.value = true;
    try {
      while (uploadQueue.value.length > 0) {
        const task = uploadQueue.value.shift();
        if (!task) break;
        try {
          await doUpload(task.params, task.files);
          task.resolve(true);
        } catch (e) {
          task.reject(e);
        }
      }
    } finally {
      isProcessingQueue.value = false;
    }
  };

  // Public API - enqueue uploads, process in order, return a promise that resolves when done
  const uploadVideo = async (params: UploadVideoRequestBody, files: File[]) => {
    return new Promise((resolve, reject) => {
      uploadQueue.value.push({ params, files, resolve, reject });
      // Kick the processor if idle
      void processUploadQueue();
    });
  };

  const updateVideo = async (video_id: number, params: UpdateVideoRequestBody) => {
    const { fetchData, error, data } = useFetchApi();
    await fetchData(() => VideoApi.updateVideo(video_id, params));

    if (error.value) {
      throw error.value;
    }

    const response = data.value;
    if (response) {
      return true;
    } else {
      throw new ApiError(500, 'サーバーエラー');
    }
  };

  const updateVideoTieGameData = async (video_id: number, gameDataJson: string) => {
    const { fetchData, error, data } = useFetchApi();
    await fetchData(() => VideoApi.updateVideoTieGameData(video_id, gameDataJson));
    if (error.value) {
      throw error.value;
    }
    const response = data.value;
    if (response) {
      return true;
    } else {
      throw new ApiError(500, 'サーバーエラー');
    }
  };

  const deleteVideo = async (video_id: number) => {
    const { fetchData, error, data } = useFetchApi();
    await fetchData(() => VideoApi.deleteVideo(video_id));

    if (error.value) throw error.value;

    if (data.value) {
      // 1. Remove from UI
      const video_id_str = video_id.toString();
      if (currentUploads.value[video_id_str]) {
        delete currentUploads.value[video_id_str];
      }

      // 2. Tell the central worker to abort and clean up!
      worker.postMessage({ type: 'CLEANUP_VIDEO', videoId: video_id });
      return true;
    } else {
      throw new ApiError(500, 'サーバーエラー');
    }
  };

  const setSkipCache = (skip: boolean) => {
    videoListCondition.value.skip_cache = skip;
  };

  return {
    videoList,
    labelList,
    currentVideo,
    currentUploads,
    videoListCondition,
    getTotalVideoCount,
    totalPages,
    fetchVideoList,
    fetchVideo,
    uploadVideo,
    updateVideo,
    updateVideoTieGameData,
    deleteVideo,
    setSkipCache,
    resumePendingUploads
  };
});
